/**
 * Tabla de **activación por sede** de un módulo (inventario o láser).
 *
 * Presentacional: recibe las filas del overview admin y el callback de toggle por
 * sede (que la pantalla cablea a la mutación). Por sede muestra el estado
 * (Activo/Inactivo), el botón Activar/Desactivar y la última actualización
 * (quién/cuándo). Réplica de las tablas de `admin_inventario.html`. La fila cuyo
 * toggle está en curso se deshabilita (`pendingSedeId`).
 */
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui";

import type { AdminModule, AdminSedeRow } from "../types";

interface AdminSedeTableProps {
  rows: AdminSedeRow[];
  module: AdminModule;
  /** Etiqueta accesible de la sección (para localizar la tabla en tests/lectores). */
  label: string;
  /** Id de la sede cuyo toggle está en curso (deshabilita solo esa fila). */
  pendingSedeId: number | null;
  onToggle: (sedeId: number) => void;
}

export function AdminSedeTable({ rows, module, label, pendingSedeId, onToggle }: AdminSedeTableProps) {
  if (rows.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-slate-500">
        No hay sedes activas para configurar.
      </p>
    );
  }

  return (
    <Table aria-label={label}>
      <TableHead>
        <TableRow>
          <TableHeaderCell>Sede</TableHeaderCell>
          <TableHeaderCell>Estado</TableHeaderCell>
          <TableHeaderCell>Acción</TableHeaderCell>
          <TableHeaderCell>Última actualización</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => {
          const activo = module === "inventario" ? row.inventario_activo : row.laser_activo;
          const updatedAt =
            module === "inventario" ? row.inventario_updated_at : row.laser_updated_at;
          const updatedBy =
            module === "inventario" ? row.inventario_updated_by : row.laser_updated_by;
          const isPending = pendingSedeId === row.id;
          return (
            <TableRow key={row.id}>
              <TableCell className="font-medium text-slate-800">{row.nombre}</TableCell>
              <TableCell>
                <span
                  className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    activo ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {activo ? "Activo" : "Inactivo"}
                </span>
              </TableCell>
              <TableCell>
                <Button
                  type="button"
                  variant={activo ? "danger" : "primary"}
                  size="sm"
                  disabled={isPending}
                  onClick={() => onToggle(row.id)}
                  aria-label={`${activo ? "Desactivar" : "Activar"} ${
                    module === "inventario" ? "inventario" : "horario láser"
                  } en ${row.nombre}`}
                >
                  {isPending ? "Guardando…" : activo ? "Desactivar" : "Activar"}
                </Button>
              </TableCell>
              <TableCell className="text-slate-500">
                {updatedAt && updatedAt !== "—" ? updatedAt : "—"}
                {updatedBy && updatedBy !== "—" ? ` · ${updatedBy}` : ""}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
